import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import apiClient from '../api/client';
import { CATEGORIES, CATEGORY_COLORS, formatDate } from '../utils/helpers';
import useWindowWidth from '../hooks/useWindowWidth';
import { PRIMARY } from '../styles/shared';
import { EmptyJournalIllustration, SearchEmptyIllustration } from '../components/Illustrations';
import ConfirmDialog from '../components/ConfirmDialog';
import { trackEvent } from '../utils/analytics';

const styles = {
  page: { minHeight: '100vh', backgroundColor: '#f8fafc' },
  content: { maxWidth: '900px', margin: '0 auto', padding: '2rem 1.5rem' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' },
  title: { fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' },
  headerBtns: { display: 'flex', gap: '0.75rem' },
  btn: {
    padding: '0.75rem 1.1rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    border: 'none',
    transition: 'opacity 0.15s',
    minHeight: '44px',
  },
  primaryBtn: { backgroundColor: PRIMARY, color: '#ffffff' },
  outlineBtn: { backgroundColor: '#ffffff', color: '#64748b', border: '1px solid #e2e8f0' },
  filterBar: {
    display: 'flex',
    gap: '0.75rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  select: {
    padding: '0.5rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    color: '#1e293b',
    backgroundColor: '#ffffff',
    outline: 'none',
  },
  searchInput: {
    padding: '0.5rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    color: '#1e293b',
    outline: 'none',
  },
  dateInput: {
    padding: '0.5rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    color: '#1e293b',
    outline: 'none',
  },
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '1.25rem',
    marginBottom: '0.75rem',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s',
    position: 'relative',
  },
  cardHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.5rem' },
  cardTitle: { fontWeight: '600', color: '#1e293b', fontSize: '1rem', flex: 1, marginRight: '1rem' },
  cardMeta: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' },
  date: { fontSize: '0.8rem', color: '#94a3b8' },
  badge: (color) => ({
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '999px',
    backgroundColor: color + '1a',
    color: color,
    fontSize: '0.75rem',
    fontWeight: '600',
  }),
  preview: { fontSize: '0.875rem', color: '#64748b', lineHeight: '1.5', marginBottom: '0.6rem' },
  tags: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap' },
  tag: {
    padding: '0.15rem 0.5rem',
    borderRadius: '4px',
    backgroundColor: '#f1f5f9',
    color: '#64748b',
    fontSize: '0.75rem',
  },
  deleteBtn: {
    padding: '0.35rem 0.75rem',
    backgroundColor: '#fef2f2',
    color: '#ef4444',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: '500',
    cursor: 'pointer',
    flexShrink: 0,
  },
  pagination: { display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1.5rem' },
  pageBtn: (active) => ({
    padding: '0.6rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: active ? '600' : '400',
    backgroundColor: active ? PRIMARY : '#ffffff',
    color: active ? '#ffffff' : '#1e293b',
    minHeight: '44px',
  }),
  emptyState: {
    textAlign: 'center',
    padding: '4rem 2rem',
    color: '#64748b',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    border: '1px solid #e2e8f0',
  },
  loadingText: { textAlign: 'center', color: '#64748b', padding: '3rem' },
  error: {
    color: '#ef4444',
    fontSize: '0.875rem',
    padding: '0.75rem',
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
};

const LIMIT = 10;

// T25: build a YYYY-MM-DD string in local time. Avoid Date.toISOString() so
// users near midnight in non-UTC timezones don't see the wrong day.
function toLocalDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// T25: compute [start, end] strings for a chip key. Returns ['', ''] for
// "All time" (clears the date filter). Week starts on Monday.
function rangeForChip(key) {
  const today = new Date();
  const endStr = toLocalDateStr(today);
  if (key === 'this-week') {
    const d = new Date(today);
    // Monday-of-this-week. getDay() returns 0 for Sunday → treat as 7.
    const dow = d.getDay() === 0 ? 7 : d.getDay();
    d.setDate(d.getDate() - (dow - 1));
    return [toLocalDateStr(d), endStr];
  }
  if (key === 'this-month') {
    const d = new Date(today.getFullYear(), today.getMonth(), 1);
    return [toLocalDateStr(d), endStr];
  }
  if (key === 'last-3-months') {
    const d = new Date(today);
    d.setMonth(d.getMonth() - 3);
    return [toLocalDateStr(d), endStr];
  }
  // 'all-time' or anything else → clear
  return ['', ''];
}

const DATE_CHIPS = [
  { key: 'this-week', label: 'This week' },
  { key: 'this-month', label: 'This month' },
  { key: 'last-3-months', label: 'Last 3 months' },
  { key: 'all-time', label: 'All time' },
];

export default function JournalPage() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState('');
  const [activeTag, setActiveTag] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // T25: which quick-filter chip is currently applied. Stays highlighted
  // until the user manually changes a date input (which clears it back to null).
  const [activeDateChip, setActiveDateChip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allTags, setAllTags] = useState([]);
  const { isMobile } = useWindowWidth();

  const searchDebounceRef = useRef(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (category) params.set('category', category);
      if (activeTag) params.set('tag', activeTag);
      if (search) params.set('search', search);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      const res = await apiClient.get(`/api/entries?${params}`);
      const fetchedEntries = res.data.data?.entries || [];
      const fetchedTotal   = res.data.data?.total   || 0;
      setEntries(fetchedEntries);
      setTotal(fetchedTotal);
      // Bounds check: if the current page is empty but total > 0, we are past
      // the last page. Reset to page 1 to show results.
      if (fetchedEntries.length === 0 && fetchedTotal > 0 && page > 1) {
        setPage(1);
      }
    } catch {
      setError('Failed to load entries.');
    } finally {
      setLoading(false);
    }
  }, [page, category, activeTag, search, startDate, endDate]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchEntries();
    }, search ? 300 : 0);
    return () => clearTimeout(searchDebounceRef.current);
  }, [fetchEntries, search]);

  useEffect(() => {
    apiClient.get('/api/entries/tags')
      .then(res => setAllTags(res.data.data?.tags || []))
      .catch(() => setAllTags([]));
  }, []);

  // Two-step delete: button click sets pendingDeleteId; ConfirmDialog
  // confirm performs the actual deletion. Replaces window.confirm() so
  // the prompt is in-app (consistent UX, dismissible by Escape / click-out).
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    setPendingDeleteId(id);
  };

  const handleConfirmDelete = async () => {
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    if (!id) return;
    try {
      await apiClient.delete(`/api/entries/${id}`);
      fetchEntries();
    } catch {
      setError('Failed to delete entry.');
    }
  };

  const [exporting, setExporting] = useState(false);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get('/api/export/report', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bragsheet-report.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      // exported_pdf: fires only after the download is triggered successfully.
      trackEvent('exported_pdf');
    } catch {
      setError('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const safeTotalPages = totalPages || 1;

  return (
    <div style={styles.page}>
      <NavBar />
      <div id="main-content" tabIndex={-1} style={styles.content}>
        <div style={styles.header}>
          <h1 style={styles.title}>Journal</h1>
          <div style={styles.headerBtns}>
            <button
              style={{ ...styles.btn, ...styles.outlineBtn, ...(exporting ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
              onClick={handleExportPdf}
              disabled={exporting}
              aria-label="Export PDF Report"
            >
              {exporting ? 'Generating...' : 'Export PDF Report'}
            </button>
            <button style={{ ...styles.btn, ...styles.primaryBtn }} onClick={() => navigate('/journal/new')}>
              + New Entry
            </button>
          </div>
        </div>

        <div style={{
          ...styles.filterBar,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
        }}>
          <select
            style={{
              ...styles.select,
              width: isMobile ? '100%' : 'auto',
            }}
            value={category}
            onChange={e => { setCategory(e.target.value); setActiveTag(''); setPage(1); }}
            aria-label="Filter by category"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div style={{ position: 'relative', minWidth: isMobile ? 0 : '220px', width: isMobile ? '100%' : 'auto' }}>
            <input
              style={{
                ...styles.searchInput,
                width: '100%',
                paddingRight: search ? '2rem' : '0.875rem',
              }}
              type="text"
              placeholder="Search entries..."
              value={search}
              onChange={e => {
                const val = e.target.value;
                setSearch(val);
                setPage(1);
                if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
              }}
              aria-label="Search entries"
            />
            {search && (
              <button
                type="button"
                onClick={() => { setSearch(''); setPage(1); }}
                aria-label="Clear search"
                style={{
                  position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#94a3b8', fontSize: '1rem', padding: '0.25rem',
                  lineHeight: '1',
                }}
              >
                ✕
              </button>
            )}
          </div>
          {/* T25: quick-filter chips. Active chip uses teal background; */}
          {/* inactive chips show a transparent border. Manually editing a   */}
          {/* date input clears the active chip (see onChange handlers).    */}
          <div
            role="group"
            aria-label="Quick date filters"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.4rem',
              width: '100%',
              marginBottom: '0.25rem',
            }}
          >
            {DATE_CHIPS.map(chip => {
              const active = activeDateChip === chip.key;
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => {
                    const [s, e] = rangeForChip(chip.key);
                    setStartDate(s);
                    setEndDate(e);
                    setActiveDateChip(chip.key);
                    setPage(1);
                  }}
                  aria-pressed={active}
                  style={{
                    padding: '0.35rem 0.85rem',
                    borderRadius: '999px',
                    border: active ? `1px solid ${PRIMARY}` : '1px solid transparent',
                    backgroundColor: active ? PRIMARY : '#f1f5f9',
                    color: active ? '#ffffff' : '#64748b',
                    fontSize: '0.8rem',
                    fontWeight: active ? '600' : '500',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
          <input
            style={{
              ...styles.dateInput,
              width: isMobile ? '100%' : 'auto',
            }}
            type="date"
            value={startDate}
            onChange={e => { setStartDate(e.target.value); setActiveDateChip(null); setPage(1); }}
            title="From date"
            placeholder="From"
            aria-label="Filter from date"
          />
          <input
            style={{
              ...styles.dateInput,
              width: isMobile ? '100%' : 'auto',
            }}
            type="date"
            value={endDate}
            onChange={e => { setEndDate(e.target.value); setActiveDateChip(null); setPage(1); }}
            title="To date"
            placeholder="To"
            aria-label="Filter to date"
          />
          {allTags.length > 0 && (
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', width: '100%', marginTop: '0.5rem' }}>
              {allTags.map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => { setActiveTag(activeTag === t ? '' : t); setPage(1); }}
                  style={{
                    padding: '0.2rem 0.6rem',
                    borderRadius: '999px',
                    border: `1px solid ${activeTag === t ? PRIMARY : '#e2e8f0'}`,
                    backgroundColor: activeTag === t ? PRIMARY : '#f1f5f9',
                    color: activeTag === t ? '#ffffff' : '#64748b',
                    fontSize: '0.75rem',
                    fontWeight: activeTag === t ? '600' : '400',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  #{t}
                </button>
              ))}
              {activeTag && (
                <button
                  type="button"
                  onClick={() => { setActiveTag(''); setPage(1); }}
                  style={{
                    padding: '0.2rem 0.6rem',
                    borderRadius: '999px',
                    border: '1px solid #fecaca',
                    backgroundColor: '#fef2f2',
                    color: '#ef4444',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>

        {error && (
          <div role="alert" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', borderRadius: '10px', padding: '2rem', textAlign: 'center', marginBottom: '1rem' }}>
            <p style={{ marginBottom: '0.75rem' }}>{error}</p>
            <button onClick={fetchEntries} style={{ ...styles.btn, ...styles.primaryBtn, padding: '0.5rem 1rem' }}>Retry</button>
          </div>
        )}

        {loading ? (
          <p style={styles.loadingText}>Loading entries...</p>
        ) : entries.length === 0 ? (
          (search || category || activeTag || startDate || endDate) ? (
            <div style={styles.emptyState}>
              <div style={{ marginBottom: '1rem' }}><SearchEmptyIllustration size={120} /></div>
              <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>No entries match your filters</p>
              <p style={{ fontSize: '0.875rem', marginBottom: '1rem', color: '#94a3b8' }}>Try adjusting your search or filters</p>
              <button
                style={{ ...styles.btn, ...styles.primaryBtn }}
                onClick={() => { setSearch(''); setCategory(''); setActiveTag(''); setStartDate(''); setEndDate(''); setActiveDateChip(null); setPage(1); }}
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <div style={styles.emptyState}>
              <div style={{ marginBottom: '1rem' }}><EmptyJournalIllustration size={120} /></div>
              <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>No entries yet. Start journaling!</p>
              <button
                style={{ ...styles.btn, ...styles.primaryBtn }}
                onClick={() => navigate('/journal/new')}
              >
                Write Your First Entry
              </button>
            </div>
          )
        ) : (
          <>
            {entries.map(entry => {
              const color = CATEGORY_COLORS[entry.category] || '#94a3b8';
              const tags = Array.isArray(entry.tags)
                ? entry.tags
                : (typeof entry.tags === 'string' ? JSON.parse(entry.tags || '[]') : []);
              return (
                <div
                  key={entry.id}
                  style={{ ...styles.card, borderLeft: `4px solid ${color}` }}
                  onClick={() => navigate(`/journal/${entry.id}`)}
                >
                  <div style={{
                    ...styles.cardHeader,
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                  }}>
                    <span style={{
                      ...styles.cardTitle,
                      width: isMobile ? '100%' : 'auto',
                      marginRight: isMobile ? 0 : '1rem',
                      marginBottom: isMobile ? '0.4rem' : 0,
                    }}>{entry.title}</span>
                    <button
                      style={styles.deleteBtn}
                      onClick={e => handleDeleteClick(e, entry.id)}
                      aria-label={`Delete entry: ${entry.title}`}
                    >
                      Delete
                    </button>
                  </div>
                  <div style={styles.cardMeta}>
                    <span style={styles.badge(color)}>{entry.category}</span>
                    <span style={styles.date}>{formatDate(entry.entryDate || entry.createdAt)}</span>
                  </div>
                  <p style={styles.preview}>
                    {entry.content?.substring(0, 150)}{entry.content?.length > 150 ? '...' : ''}
                  </p>
                  {tags.length > 0 && (
                    <div style={styles.tags}>
                      {tags.map((t, i) => <span key={i} style={styles.tag}>#{t}</span>)}
                    </div>
                  )}
                </div>
              );
            })}

            {safeTotalPages > 1 && (
              <div style={styles.pagination}>
                {Array.from({ length: safeTotalPages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    style={styles.pageBtn(p === page)}
                    onClick={() => setPage(p)}
                    aria-label={`Page ${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <ConfirmDialog
        open={pendingDeleteId !== null}
        title="Delete this entry?"
        message="This entry will be permanently removed from your journal. This can't be undone."
        confirmText="Delete entry"
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDeleteId(null)}
      />
    </div>
  );
}
